/**
 * UtilizationTab
 *
 * Shows how much of a firm's total billed hours came from Hourglass vs the
 * time & billing system. Requires billing transaction data to be synced.
 *
 * Sections:
 * - SyncWarningBanner (shown when billing data is stale)
 * - UtilizationKpiCards (Firm %, Hourglass hrs, Total billing hrs)
 * - UtilizationChart (stacked bar per attorney, timeframe slider in chart header)
 */

import { useState, useMemo } from "react";
import { useUtilization } from "../../hooks/useAdminData";
import { getDateRangeFromTimeframe } from "../../types/dashboard";
import UtilizationKpiCards from "./UtilizationKpiCards";
import UtilizationChart from "./UtilizationChart";
import loaderStyles from "@/shared/components/LoadingPopup/LoadingPopup.module.css";

// ─────────────────────────────────────────────
// SyncWarningBanner
// ─────────────────────────────────────────────

function SyncWarningBanner({ lastSynced }: { lastSynced: string | null }) {
  const isStale = useMemo(() => {
    if (!lastSynced) return true;
    const syncDate = new Date(lastSynced);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return syncDate < sevenDaysAgo;
  }, [lastSynced]);

  if (!isStale) return null;

  const displayDate = lastSynced
    ? new Date(lastSynced).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "never";

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
      Billing data last synced {displayDate}. Utilization figures may be
      incomplete.
    </div>
  );
}

// ─────────────────────────────────────────────
// UtilizationTab
// ─────────────────────────────────────────────

export default function UtilizationTab() {
  const [timeframe, setTimeframe] = useState("30days");
  const { startDate, endDate } = getDateRangeFromTimeframe(timeframe);
  const { utilization, loading, isPlaceholderData } = useUtilization(startDate, endDate);

  // Hard loading: no data at all — first visit or cache miss
  const hardLoading = loading && !isPlaceholderData;

  return (
    <div className="space-y-4">
      {/* Centered spinner — first load only */}
      {hardLoading && (
        <div className="flex items-center justify-center min-h-[70vh]">
          <div className={loaderStyles.loader} />
        </div>
      )}

      {/* Content — dimmed while old date range data shows during a filter change */}
      {!hardLoading && (
        <div
          className={`space-y-4 transition-opacity duration-200${isPlaceholderData ? " opacity-60" : ""}`}
        >
          {utilization ? (
            <>
              <SyncWarningBanner lastSynced={utilization.billing_last_synced} />
              <UtilizationKpiCards
                totals={utilization.totals}
                attorneys={utilization.attorneys}
              />
              <UtilizationChart
                attorneys={utilization.attorneys}
                timeframe={timeframe}
                onTimeframeChange={setTimeframe}
              />
            </>
          ) : (
            <UtilizationChart
              attorneys={[]}
              timeframe={timeframe}
              onTimeframeChange={setTimeframe}
            />
          )}
        </div>
      )}
    </div>
  );
}
